"use client";
import { useEffect } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { getLocaleFromTimezone, localePath, locales, defaultLocale, type Locale } from '@/lib/i18n';
import { hasManualLocale, setAutoLocale, consumeLandingVisit } from '@/lib/localePreference';

/**
 * Locale is decided from the browser timezone, client-side — no IP lookup.
 * Only runs its auto-correction when this mount followed middleware's
 * generic legacy-unprefixed → locale-prefixed redirect (see
 * consumeLandingVisit / LOCALE_LANDING_COOKIE) — i.e. nobody actually
 * chose this locale yet. A URL that already explicitly names its locale
 * (every ordinary navigation, since every locale is always prefixed) is
 * left alone. Also never overrides a locale picked manually via the
 * switcher (lib/localePreference.ts).
 *
 * On an fa-default deployment (iran build), a timezone that isn't
 * Iran/Afghanistan/Tajikistan and doesn't map to any other available
 * locale either falls back to en (rather than sitting on fa, which would
 * otherwise be every unmatched visitor's landing locale just because it's
 * this deployment's default) — English reads better as the generic
 * fallback for a visitor Persian clearly isn't meant for.
 *
 * Writes the resolved locale to the locale-pref cookie every time it does
 * run, so middleware.ts can redirect *before* render on the visitor's
 * next legacy-URL landing.
 *
 * In practice this rarely fires anymore: lib/timezoneRedirectScript.ts
 * (a render-blocking inline <script> in <head>, see app/fa/layout.tsx /
 * app/[locale]/layout.tsx) runs the identical algorithm before <body> is
 * even parsed and consumes the same one-shot landing cookie first — this
 * component is what's left running afterward, so it just no-ops. It stays
 * as a fallback for the (very unlikely) case that inline scripts don't
 * run but hydration still does.
 */
export default function TimezoneLocaleSync({ locale }: { locale: Locale }) {
    const router = useRouter();
    const pathname = usePathname();

    useEffect(() => {
        if (hasManualLocale()) return;
        if (!consumeLandingVisit()) return;

        let timeZone: string;
        try {
            timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        } catch {
            return;
        }

        const mapped = getLocaleFromTimezone(timeZone)
            ?? (defaultLocale === 'fa' && locales.includes('en') ? 'en' : null);
        if (!mapped) return;

        setAutoLocale(mapped);
        if (mapped === locale) return;

        const rest = pathname.startsWith(`/${locale}/`) || pathname === `/${locale}`
            ? pathname.slice(`/${locale}`.length)
            : pathname === '/' ? '' : pathname;
        router.replace(localePath(mapped, rest.replace(/^\//, '')));
    }, [locale, pathname, router]);

    return null;
}
